import React, { useEffect, useRef, useState } from 'react';
import { Select } from 'antd';

import { fetchTagOptions } from './api';
import { TagOption } from './types';

/** What each AI tag category means to an administrator. */
export const CATEGORY_LABELS: Record<string, string> = {
  ai_geography: 'Geography',
  ai_event: 'Event',
  ai_indicator: 'Indicator',
  ai_institution: 'Institution',
  ai_instrument: 'Instrument',
  ai_curation: 'Selection',
};

export const tagOptionLabel = (tag: TagOption) =>
  `${CATEGORY_LABELS[tag.category] || tag.category}: ${tag.label || tag.text}`;

type Props = {
  value: TagOption[];
  onChange: (value: TagOption[]) => void;
  disabled?: boolean;
};

/** Narrows AI Curated news by its tags: every chosen tag must be on a story. */
const TagFilter: React.FC<Props> = ({ value, onChange, disabled }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<TagOption[]>([]);
  const [loading, setLoading] = useState(false);
  const request = useRef<AbortController | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      request.current?.abort();
      const controller = new AbortController();
      request.current = controller;
      setLoading(true);
      fetchTagOptions(query.trim(), controller.signal)
        .then(page => {
          if (!controller.signal.aborted) setOptions(page.results);
        })
        .catch(() => {
          if (!controller.signal.aborted) setOptions([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);
  useEffect(() => () => request.current?.abort(), []);

  const known = new Map(
    [...options, ...value].map(tag => [tag.id, tag] as const),
  );
  return (
    <Select
      mode="multiple"
      aria-label="Filter by tags"
      placeholder="Search tags: country, event, indicator, institution, instrument"
      value={value.map(tag => tag.id)}
      onChange={(ids: number[]) =>
        onChange(
          ids
            .map(id => known.get(id))
            .filter((tag): tag is TagOption => Boolean(tag)),
        )
      }
      showSearch
      filterOption={false}
      onSearch={setQuery}
      loading={loading}
      disabled={disabled}
      allowClear
      maxCount={10}
      style={{ minWidth: 320, flex: 1 }}
      notFoundContent={loading ? 'Searching…' : 'No matching tags'}
      options={[...known.values()].map(tag => ({
        value: tag.id,
        label: tagOptionLabel(tag),
      }))}
    />
  );
};

export default TagFilter;
